import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

// fonts
import 'assets/fonts/inter/inter.css';

// global brand tokens (Sothema)
import 'brand/brand.css';
import 'styles/shadcn.css';

// scroll bar
import 'simplebar/dist/simplebar.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// google-fonts
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/700.css';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';

// project-imports
import App from './App';
import { ConfigProvider } from 'contexts/ConfigContext';
import reportWebVitals from './reportWebVitals';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
const sentryEnvironment =
  import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development';
const sentryRelease = import.meta.env.VITE_RELEASE || undefined;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    release: sentryRelease,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.05),
    replaysSessionSampleRate: Number(
      import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE || 0.0
    ),
    replaysOnErrorSampleRate: Number(
      import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE || 1.0
    ),
    beforeSend(event, hint) {
      const message = hint?.originalException?.message || event?.message || '';
      const ignoredPatterns = [
        /ResizeObserver loop limit exceeded/i,
        /NetworkError/i,
        /Load failed/i,
        /AbortError/i,
        /Non-Error promise rejection captured/i,
      ];

      if (ignoredPatterns.some((pattern) => pattern.test(String(message)))) {
        return null;
      }

      return event;
    },
  });
}

// ==============================|| CHUNK LOAD ERROR HANDLER ||============================== //
// Auto-refresh when cached JS tries to load chunks that no longer exist after a rebuild

// Only actually reload once per 10s (separate from whether we suppress the
// crash below — that must always happen, reloading is just the secondary,
// rate-limited recovery step).
const refreshForStaleBuild = () => {
  const lastRefresh = sessionStorage.getItem('chunk_error_refresh');
  const now = Date.now();

  if (!lastRefresh || now - parseInt(lastRefresh, 10) > 10000) {
    sessionStorage.setItem('chunk_error_refresh', now.toString());
    console.warn('Stale build asset detected, refreshing page to get latest version...');
    window.location.reload();
    return true;
  }
  return false;
};

const chunkFailedMessage = /Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|Unable to preload CSS/;

// Global error handler for uncaught chunk loading errors
window.addEventListener('error', (event) => {
  const error = event.error || { message: event.message };
  if (chunkFailedMessage.test(error.message)) {
    event.preventDefault();
    refreshForStaleBuild();
  }
});

// Handle unhandled promise rejections (dynamic imports fail as rejected promises)
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && chunkFailedMessage.test(event.reason.message || '')) {
    event.preventDefault();
    refreshForStaleBuild();
  }
});

// Vite's own event for this whole class of error (any failed module/CSS
// preload, regardless of exact wording). MUST always call preventDefault()
// here, unconditionally — Vite's preload helper only throws (which is what
// React Router's error boundary then catches as a hard crash) when nothing
// calls preventDefault(); gating it behind the refresh debounce (as an
// earlier version of this code did) meant the 2nd+ occurrence within 10s
// threw for real instead of being suppressed. Vite still runs the actual
// lazy import afterward regardless, so the component can mount even if
// this one CSS file didn't load — reloading is extra recovery on top of
// that, not what prevents the crash.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  refreshForStaleBuild();
});

// Vite's own event for this whole class of error (any failed module/CSS
// preload, regardless of exact wording) — more robust than matching on
// error.message text, which could change between Vite versions.
window.addEventListener('vite:preloadError', (event) => {
  if (refreshForStaleBuild()) {
    event.preventDefault();
  }
});

const container = document.getElementById('root');
const root = createRoot(container);

// ==============================|| MAIN - REACT DOM RENDER  ||============================== //

root.render(
  <ConfigProvider>
    <App />
  </ConfigProvider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
